import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { ArrowLeft, Printer, DownloadSimple, ArrowSquareOut, ArrowCounterClockwise } from "@phosphor-icons/react";
import { ItemCard, PriceInput, money, bare } from "@/components/admin/invoice/ItemCard";
import { ProfitPage } from "@/components/admin/invoice/ProfitPage";
import { DeleteRequestButton } from "@/components/admin/invoice/DeleteRequestButton";
import { ReviewCard } from "@/components/admin/invoice/ReviewCard";
import { AddSetForm } from "@/components/admin/invoice/AddSetForm";

const PAY = { venmo: "Venmo", cashapp: "Cash App", paypal: "PayPal", local: "Local / Cash" };

/** Internal invoice — pictures, market data at quote time, editable payouts, then a projection page. */
export default function RequestDetail({ standalone = false, backTo = "/invoices" }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  useEffect(() => { adminApi.request(id).then(setR).catch(() => toast.error("Request not found")); }, [id]);
  useEffect(() => { if (standalone && r) document.title = `${r.order_no || r.request_id} · Internal invoice`; }, [standalone, r]);
  const adjust = async (body, ok) => {
    try { setR(await adminApi.adjustPrices(id, body)); toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't adjust prices"); setR(await adminApi.request(id)); }
  };
  const patch = async (body) => {
    try { await adminApi.patchRequest(id, body); setR(await adminApi.request(id)); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  const removeSet = async (sn) => {
    try { setR(await adminApi.removeRequestItem(id, sn)); toast.success(`#${bare(sn)} removed`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove set"); }
  };
  const addSet = async (sn, qty) => {
    try {
      const next = await adminApi.addRequestItem(id, { set_number: sn, qty });
      setR(next);
      const hit = (next.items || []).find(i => i.set_number.startsWith(sn.replace(/-\d+$/, "")));
      toast.success(hit ? `#${bare(hit.set_number)} added at ${money(hit.offer)} × ${qty}` : `#${sn} added for review — set its market data or pin a payout below`);
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add set"); return false; }
  };
  const setMarket = async (sn, body) => {
    try {
      const next = await adminApi.setItemMarket(id, sn, body);
      setR(next);
      const hit = (next.items || []).find(i => i.set_number === sn);
      toast.success(hit ? `#${bare(sn)} now ${money(hit.offer)} / unit` : `#${bare(sn)} still needs review — rules couldn't price it`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update market data"); }
  };
  if (!r) return <div className="p-8 font-mono text-sm text-gray-500">Loading…</div>;
  const a = r.analytics || {};
  const created = new Date(r.created_at);
  const adjusted = r.quoted_total != null && Math.abs(r.quoted_total - r.total) > 0.004;
  return (
    <div className={standalone ? "min-h-screen bg-white text-gray-900" : ""} data-testid={standalone ? "request-standalone" : undefined}>
      {standalone && (
        <>
          <div className="h-2 w-full print:h-1" style={{ background: "linear-gradient(90deg, #E3000B 0 25%, #FFD500 25% 50%, #0055BF 50% 75%, #00A850 75%)" }} />
          <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between">
            <img src="/brand/offers4bricks-logo@1000.webp" alt="OFFERS4BRICKS" className="h-10 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-gray-500">INTERNAL · {created.toLocaleDateString()}</span>
          </div>
        </>
      )}
    <div className={`p-6 max-w-6xl space-y-5 ${standalone ? "mx-auto" : ""}`} data-testid="request-detail">
      <div className="flex items-center gap-3 no-print">
        {standalone
          ? <button onClick={() => window.close()} className="font-mono text-xs text-gray-500 hover:text-black" data-testid="request-close">Close tab</button>
          : <Link to={backTo} className="font-mono text-xs text-gray-500 hover:text-black inline-flex items-center gap-1" data-testid="request-back"><ArrowLeft size={12} /> All invoices</Link>}
        <div className="ml-auto flex gap-2 flex-wrap">
          <DeleteRequestButton request={r} onDeleted={() => standalone ? window.close() : nav(backTo)} />
          {!standalone && <a href={`/admin/invoice/${r.request_id}`} target="_blank" rel="noopener noreferrer" className="h-8 px-3 inline-flex items-center gap-1 rounded-sm border border-black font-mono text-xs hover:bg-black hover:text-white" data-testid="request-open-full"><ArrowSquareOut size={12} /> Full view (new tab)</a>}
          <a href={adminApi.accelerlistCsvUrl(r.request_id)} className="h-8 px-3 inline-flex items-center gap-1 rounded-sm border border-[#0055BF]/40 text-[#0055BF] font-mono text-xs hover:bg-[#0055BF]/10" data-testid="request-accelerlist-csv"><DownloadSimple size={12} /> AccelerList CSV</a>
          {r.order_no && r.invoice_token && <a href={`/invoice/${r.order_no}/${r.invoice_token}`} target="_blank" rel="noopener noreferrer" className="h-8 px-3 inline-flex items-center gap-1 rounded-sm border border-[#E4E4E0] font-mono text-xs hover:border-black" data-testid="request-customer-invoice"><ArrowSquareOut size={12} /> Customer invoice</a>}
          <button onClick={() => window.print()} className="h-8 px-3 inline-flex items-center gap-1 rounded-sm bg-black text-white font-mono text-xs" data-testid="request-print"><Printer size={12} /> Print</button>
        </div>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4 border-b-2 border-black pb-4">
        <div>
          <div className="text-[10px] font-black tracking-[0.25em] text-gray-500">{r.source === "deals_upload" ? `DEAL · ${r.upload?.filename || "spreadsheet upload"} · AUTO-ACCEPTED` : "INTERNAL INVOICE · NOT FOR CUSTOMER"}</div>
          <div className="font-head text-3xl font-black tracking-tight" data-testid="request-order-no">{r.order_no || r.request_id}</div>
          <div className="font-mono text-xs text-gray-600">{created.toLocaleString()} · ref {r.request_id}{r.quote_id ? ` · quote ${r.quote_id}` : ""}</div>
        </div>
        <div className="flex items-end gap-6">
          <Stat label="Units" value={r.units} />
          <Stat label="Status" value={<span className="uppercase">{r.status}</span>} />
          <div className="text-right">
            <div className="text-[10px] font-black tracking-widest text-gray-500">TOTAL PAYOUT · EDIT TO SPREAD</div>
            <PriceInput big value={r.total} testId="request-total-input" onCommit={(n) => adjust({ total: n }, `Total set to ${money(n)} — spread across unpinned sets`)} />
            <div className="font-mono text-[10px] text-gray-500 mt-1 flex items-center justify-end gap-2" data-testid="request-total">
              {adjusted ? <>quoted {money(r.quoted_total)} <button onClick={() => adjust({ reset: true }, "Prices restored to the original quote")} className="no-print inline-flex items-center gap-1 text-[#E3000B] hover:underline" data-testid="request-reset-prices"><ArrowCounterClockwise size={10} /> reset</button></> : <span>{money(r.total)} · as quoted</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Block title="Seller · always editable">
          <SellerField label="Name" value={r.customer?.name} onSave={(v) => patch({ customer: { name: v } })} testId="seller-name" />
          <SellerField label="Email" value={r.customer?.email} onSave={(v) => patch({ customer: { email: v } })} testId="seller-email" type="email" />
          <SellerField label="Phone" value={r.customer?.phone} onSave={(v) => patch({ customer: { phone: v } })} testId="seller-phone" />
          <SellerField label="Location / ZIP" value={r.customer?.location || r.customer?.zip} onSave={(v) => patch({ customer: { location: v, zip: v } })} testId="seller-location" />
          <SellerField label="Notes" value={r.notes} onSave={(v) => patch({ notes: v })} testId="seller-notes" multiline />
        </Block>
        <Block title="Payout">
          <label className="block"><span className="text-[9px] font-black tracking-widest text-gray-500 uppercase">Method</span>
            <select value={r.payment_method || ""} onChange={(e) => patch({ payment_method: e.target.value })} className="w-full bg-transparent border-b border-dashed border-[#E4E4E0] focus:border-black outline-none font-mono text-xs py-0.5" data-testid="request-pay-method">
              <option value="" disabled>— not chosen</option>{Object.entries(PAY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          {r.payment_method === "local"
            ? <SellerField label="Pickup city / state" value={r.customer?.location || [r.customer?.city, r.customer?.state].filter(Boolean).join(", ")} onSave={(v) => patch({ customer: { location: v } })} testId="request-pay-handle" />
            : <SellerField label={r.payment_method === "cashapp" ? "$cashtag" : r.payment_method === "paypal" ? "PayPal email" : "Handle"} value={r.payout_handle} onSave={(v) => patch({ payout_handle: v })} testId="request-pay-handle" />}
          <div><span className="text-gray-500">Invoice email:</span> {r.email_status || "pending"}{r.email_sent_at ? ` · ${new Date(r.email_sent_at).toLocaleString()}` : ""}</div>
          {adjusted && <div className="text-[#D97706] text-xs no-print">Prices changed since the last email — use "Resend invoice email" on the requests list when you're done.</div>}
        </Block>
        <Block title="Market snapshot (at quote)">
          {r.bonus_pct ? <div className="text-[#00A850] font-bold" data-testid="request-bonus">★ {r.bonus_label || "Loyalty bonus"} +{r.bonus_pct}% applied to every payout</div> : null}
          <div><span className="text-gray-500">PriceCharting value:</span> <b data-testid="request-pc-total">{money(a.pc_total)}</b></div>
          {a.ask_total > 0 && <div><span className="text-gray-500">Seller asking:</span> <b data-testid="request-ask-total">{money(a.ask_total)}</b> <span className="text-gray-500">({Math.round(r.total / a.ask_total * 100)}% of ask)</span></div>}
          <div><span className="text-gray-500">Amazon Buy Box value:</span> <b>{money(a.buy_box_total)}</b></div>
          <div><span className="text-gray-500">Payout as % of PC:</span> <b>{a.payout_pct_of_pc != null ? `${a.payout_pct_of_pc}%` : "—"}</b></div>
          <div><span className="text-gray-500">Projected profit:</span> <b className={(a.projection?.profit || 0) >= 0 ? "text-[#00A850]" : "text-[#E3000B]"}>{money(a.projection?.profit)}</b> <span className="text-gray-500">({a.projection?.roi_pct ?? "—"}% ROI)</span></div>
        </Block>
      </div>

      <div className="space-y-2" data-testid="request-items">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-black tracking-widest text-gray-500">SETS · {r.items?.length || 0} PRICED{r.review_items?.length ? ` · ${r.review_items.length} FOR REVIEW` : ""}</div>
          <div className="font-mono text-[10px] text-gray-500 no-print">Edit a price to pin it · edit the total to spread the change across unpinned sets</div>
        </div>
        {(r.items || []).map(i => <ItemCard key={i.set_number} item={i} onRemove={removeSet} onMarket={setMarket} onQty={(sn, q) => adjust({ items: [{ set_number: sn, qty: q }] }, `#${bare(sn)} quantity set to ${q}`)} onPrice={(sn, n) => adjust({ items: [{ set_number: sn, offer: n }] }, `#${bare(sn)} set to ${money(n)}`)} />)}
        {(r.review_items || []).map(i => <ReviewCard key={`rv-${i.set_number}`} item={i} onRemove={removeSet} onMarket={setMarket} />)}
        <AddSetForm onAdd={addSet} />
      </div>

      {r.admin_notes && <Block title="Internal notes"><div className="whitespace-pre-wrap">{r.admin_notes}</div></Block>}

      <ProfitPage request={r} />
    </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="border border-[#E4E4E0] rounded-sm p-3 text-sm space-y-1">
      <div className="text-[10px] font-black tracking-widest text-gray-500 mb-1">{title.toUpperCase()}</div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[10px] font-black tracking-widest text-gray-500">{label.toUpperCase()}</div>
      <div className="font-head font-black text-lg">{value}</div>
    </div>
  );
}

function SellerField({ label, value, onSave, testId, type = "text", multiline }) {
  const commit = (e) => { const v = e.target.value.trim(); if (v !== (value || "")) onSave(v); };
  const cls = "w-full bg-transparent border-b border-dashed border-[#E4E4E0] focus:border-black outline-none font-mono text-xs py-0.5";
  return (
    <label className="block">
      <span className="text-[9px] font-black tracking-widest text-gray-500 uppercase">{label}</span>
      {multiline
        ? <textarea key={value || ""} defaultValue={value || ""} onBlur={commit} rows={2} className={cls + " resize-none"} placeholder="—" data-testid={testId} />
        : <input key={value || ""} type={type} defaultValue={value || ""} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className={cls} placeholder="—" data-testid={testId} />}
    </label>
  );
}
